import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Car, Users, MapPin, LogOut, Navigation, Phone, Truck, Receipt, Wallet
} from 'lucide-react';
import api from '../../services/api';

// Components
import DispatcherHeader from './DispatcherHeader';
import StatsCards from '../dispatcher/StatsCards';
import LiveMap from '../dispatcher/LiveMap';
import RidesList from '../dispatcher/RidesList';
import DriversList from '../dispatcher/DriversList';
import PassengersList from '../dispatcher/PassengersList';
import FleetManagement from '../dispatcher/FleetManagement';
import InvoicesPage from '../dispatcher/InvoicesPage';
import DailyClosingPage from '../dispatcher/DailyClosingPage';
import { RevenueChart, RidesChart } from '../dispatcher/Charts';
import CreateBookingModal from '../dispatcher/CreateBookingModal';
import CallLog from './CallLog';
import LiveDispatchScreen from './LiveDispatchScreen';
import QuickBookingForm from './QuickBookingForm';
import CashDeskPage from './CashDeskPage';
import RideOperationsPage from '../dispatcher/RideOperationsPage';

// Logo
const TIA_LOGO = 'https://assets.tiataxi.al/shared/logo-header.png';

// Dispatcher menu items
const menuItems = [
  { icon: LayoutDashboard, label: 'Live Dispatch', path: '/dispatcher' },
  { icon: MapPin, label: 'Harta Live', path: '/dispatcher/map' },
  { icon: Navigation, label: 'Porositë', path: '/dispatcher/rides' },
  { icon: Car, label: 'Menaxhimi Udhëtimeve', path: '/dispatcher/rides/operations' },
  { icon: Car, label: 'Shoferët', path: '/dispatcher/drivers' },
  { icon: Wallet, label: 'Cash Desk', path: '/dispatcher/cash-desk' },
  { icon: Phone, label: 'Telefonatat', path: '/dispatcher/calls' },
  { icon: Receipt, label: 'Faturat', path: '/dispatcher/invoices' },
];

const DispatcherPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  // User info from settings (same as admin)
  const [userInfo, setUserInfo] = useState({
    name: 'Dispatcher',
    email: 'dispatcher@tiataxi.al'
  });

  // Check auth and fetch settings
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    
    // Fetch settings to get admin/dispatcher name
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        if (response.data) {
          const firstName = response.data.admin_first_name || 'Dispatcher';
          const lastName = response.data.admin_last_name || '';
          setUserInfo({
            name: `${firstName} ${lastName}`.trim(),
            email: response.data.admin_email || localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : 'dispatcher@tiataxi.al'
          });
        }
      } catch (error) {
        // If settings fail, use localStorage
        const stored = localStorage.getItem('user');
        if (stored) {
          const user = JSON.parse(stored);
          setUserInfo({
            name: user.name || 'Dispatcher',
            email: user.email || 'dispatcher@tiataxi.al'
          });
        }
      }
    };

    fetchSettings();
  }, [navigate]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleCreateBooking = () => {
    setShowBookingModal(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userType');
    window.location.href = '/login';
  };

  // Handle user info update from header
  const handleUserInfoChange = (newInfo) => {
    setUserInfo(newInfo);
  };

  // Get initials for avatar
  const getInitials = () => {
    if (!userInfo?.name) return 'D';
    const parts = userInfo.name.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  };

  // Dashboard Home Component
  const DashboardHome = () => (
    <div className="space-y-6">
      <StatsCards />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <LiveMap onCreateBooking={handleCreateBooking} />
        </div>
        <div className="lg:col-span-1">
          <RidesList />
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <RevenueChart />
        <RidesChart />
      </div>
    </div>
  );

  const MapPage = () => (
    <div className="h-[calc(100vh-200px)]">
      <LiveMap onCreateBooking={handleCreateBooking} fullScreen />
    </div>
  );

  const RidesPage = () => (
    <div className="max-w-4xl">
      <RidesList />
    </div>
  );

  const DriversPage = () => (
    <div className="w-full">
      <DriversList />
    </div>
  );

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dispatcher' || path === '/dispatcher/') return 'Dashboard';
    if (path.includes('/map')) return 'Harta Live';
    if (path.includes('/rides/operations')) return 'Menaxhimi Udhëtimeve';
    if (path.includes('/rides')) return 'Porositë';
    if (path.includes('/drivers')) return 'Shoferët';
    if (path.includes('/passengers')) return 'Pasagjerët';
    if (path.includes('/calls')) return 'Telefonatat';
    if (path.includes('/invoices')) return 'Faturat';
    if (path.includes('/fleet')) return 'Flota';
    return 'Dashboard';
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #f9f9f9 0%, #ececec 100%)' }}>
      {/* Sidebar */}
      <aside className={`sidebar fixed inset-y-0 left-0 z-40 w-[260px] transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <img src={TIA_LOGO} alt="TIA TAXI" className="h-12 w-auto object-contain" />
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="px-4 mb-2">
            <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Menu Kryesor
            </span>
          </div>
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== '/dispatcher' && location.pathname.startsWith(item.path));
            
            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                className={`sidebar-link w-full text-left ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="border-t border-white/10 py-4">
          <button 
            onClick={handleLogout}
            className="sidebar-link w-full text-left text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span>Dil</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:ml-[260px] flex-1 flex flex-col">
        <DispatcherHeader 
          onMenuClick={() => setSidebarOpen(!sidebarOpen)} 
          title={getPageTitle()}
          userInfo={userInfo}
          onUserInfoChange={handleUserInfoChange}
        />
        <main className="p-6 flex-1">
          <Routes>
            <Route index element={<LiveDispatchScreen onCreateBooking={() => setShowBookingModal(true)} />} />
            <Route path="map" element={<MapPage />} />
            <Route path="rides" element={<RidesPage />} />
            <Route path="rides/operations" element={<RideOperationsPage basePath="/dispatcher" />} />
            <Route path="drivers" element={<DriversPage />} />
            <Route path="cash-desk" element={<CashDeskPage />} />
            <Route path="calls" element={<CallLog />} />
            <Route path="invoices" element={<InvoicesPage />} />
          </Routes>
        </main>
        
        {/* Footer */}
        <footer className="py-4 px-6 border-t border-gray-200 bg-white/50 backdrop-blur-sm">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <p>© 2026 TIATaxi Dispatcher Panel. Të gjitha të drejtat e rezervuara.</p>
            <div className="flex items-center gap-4">
              <span>Version 1.0.0</span>
              <span className="w-2 h-2 rounded-full bg-green-500" title="Sistemi aktiv"></span>
            </div>
          </div>
        </footer>
      </div>

      {/* Create Booking Modal - use QuickBookingForm instead */}
      <QuickBookingForm 
        isOpen={showBookingModal} 
        onClose={() => setShowBookingModal(false)} 
        onSuccess={() => {}}
      />
    </div>
  );
};

export default DispatcherPanel;
